/* eslint-disable max-lines */
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

import { isMediumDeviceAndUp } from "common/utils/browser";
import { generateUUID } from "common/utils/uuid";

import {
  trackTransfersPinClarificationClicked,
  trackTransfersPinCreated,
  trackTransfersPinDeleted,
  trackTransfersPinDeleteFailed,
  trackTransfersPinError,
  trackTransfersPinMessageSubmitted,
  trackTransfersPinNeedsClarification,
  trackTransfersPinReady,
  trackTransfersPinSelected,
  trackTransfersPinUnsupported,
  trackPinSessionGeneratedImageShown
} from "../transfersPinningAnalytics";
import {
  approvePin as approvePinApi,
  rejectPin as rejectPinApi,
  removePin as removePinApi,
  submitPinMessage,
  updatePinInstruction as updatePinInstructionApi
} from "./usePinningSession";

const MIN_MESSAGE_LENGTH = 1;
const MAX_PINS = 1;

export const PIN_COLORS = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#F59E0B", // Amber
  "#14B8A6" // Teal
];

/**
 * Gets the effective answer value for a clarification response.
 * @param {object | undefined} response - { selected, custom, isTypingCustom }
 * @returns {string | null}
 */
export function getClarificationAnswer(response) {
  if (!response) return null;
  if (response.isTypingCustom) {
    return response.custom?.trim() || null;
  }
  return response.selected || null;
}

/**
 * Core composable for managing pinning panel state and orchestration.
 * All ephemeral state lives here and is garbage-collected when the modal unmounts.
 *
 * @param {import('vue').Ref<string | null>} sessionIdRef - Reactive ref to the current session ID
 * @param {import('vue').Ref<string | null>} artworkIdRef - Reactive ref to the current artwork ID
 * @returns {object} Panel state, computed values, and handlers
 */
// eslint-disable-next-line max-lines-per-function
export function usePinningPanel(sessionIdRef, artworkIdRef, { toast, pendingInitialMessage } = {}) {
  const isDesktop = !isMediumDeviceAndUp() ? ref(false) : ref(true);

  const pins = ref([]);
  const activePinId = ref(null);
  const hoveredPinId = ref(null);
  const hoveredPinSource = ref(null);
  const composerText = ref("");
  const removingPinIds = ref(new Set());
  const pendingDeletePinId = ref(null);
  const clarificationResponses = ref({});
  const mobileView = ref("list");
  const isPanelExpanded = ref(false);
  const editingPinId = ref(null);
  const isUpdatingPinInstruction = ref(false);
  const lastEditedPinId = ref(null);
  const unansweredAbove = ref(0);
  const unansweredBelow = ref(0);
  const showPins = ref(true);
  const previewApproval = ref(null);
  const isApproving = ref(false);
  const isRejecting = ref(false);
  const isPreviewImageLoaded = ref(false);

  const textareaRef = ref(null);
  const pendingCursorPosition = ref(null);
  const clarificationScrollRef = ref(null);
  const messagesScrollRef = ref(null);
  const pinsContainerRef = ref(null);

  // Saved state for error recovery
  let savedClarificationResponses = null;
  let savedPinState = null;
  let savedMessages = null;
  let savedComposerText = null;
  let abortController = null;

  const activePin = computed(() => pins.value.find(p => p.id === activePinId.value) ?? null);

  const editingPin = computed(() => pins.value.find(p => p.id === editingPinId.value) ?? null);

  const hasCompletedPin = computed(() =>
    pins.value.some(pin => pin.state === "ready" || pin.state === "unsupported")
  );

  const readyPinsCount = computed(() => pins.value.filter(pin => pin.state === "ready").length);

  const hasExistingPin = computed(() => pins.value.some(pin => !pin.isNew));

  const hasAiAssistanceOngoing = computed(() =>
    pins.value.some(pin => pin.state === "processing" || pin.state === "needs_clarification")
  );

  const hasUnfinishedPin = computed(() =>
    pins.value.some(
      pin =>
        pin.state === "draft" || pin.state === "processing" || pin.state === "needs_clarification"
    )
  );

  const canAddNewPin = computed(
    () =>
      pins.value.length < MAX_PINS &&
      (!activePin.value ||
        activePin.value.state === "ready" ||
        activePin.value.state === "unsupported")
  );

  const canRepositionActivePin = computed(() => {
    if (!activePin.value) return false;
    const { state, messages } = activePin.value;
    return state === "draft" && messages.length === 0;
  });

  const lastAiMessageWithClarifications = computed(() => {
    if (!activePin.value) return null;
    const { messages } = activePin.value;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg?.role === "ai" && msg.clarifications && msg.clarifications.length > 0) return msg;
    }
    return null;
  });

  const pendingPillClarifications = computed(() => {
    const clarifications = lastAiMessageWithClarifications.value?.clarifications ?? [];
    return clarifications.filter(c => c.status === "asking" && c.options.length > 0);
  });

  const fontClarification = computed(() =>
    pendingPillClarifications.value.find(c => c.type === "font_selection")
  );

  const colorClarification = computed(() =>
    pendingPillClarifications.value.find(c => c.type === "font_color")
  );

  const selectedFontValue = computed(() => {
    if (!fontClarification.value) return undefined;
    const response = clarificationResponses.value[fontClarification.value.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = fontClarification.value.options.find(o => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  });

  const selectedColorValue = computed(() => {
    if (!colorClarification.value) return undefined;
    const response = clarificationResponses.value[colorClarification.value.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = colorClarification.value.options.find(o => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  });

  const hasAnyClarificationResponse = computed(() =>
    pendingPillClarifications.value.some(c =>
      getClarificationAnswer(clarificationResponses.value[c.question])
    )
  );

  // -- Helpers --

  function updatePin(pinId, updater) {
    pins.value = pins.value.map(pin => (pin.id === pinId ? updater(pin) : pin));
  }

  function deletePin(pinId) {
    pins.value = pins.value.filter(p => p.id !== pinId);
    if (activePinId.value === pinId) {
      activePinId.value = null;
    }
    if (previewApproval.value?.pinId === pinId) {
      previewApproval.value = null;
    }
  }

  function clearRemovingPin(pinId) {
    const next = new Set(removingPinIds.value);
    next.delete(pinId);
    removingPinIds.value = next;
  }

  function isComposerTextValid(text) {
    return text.trim().length >= MIN_MESSAGE_LENGTH;
  }

  // -- Pin CRUD --

  function repositionActivePin(coordinates) {
    if (!canRepositionActivePin.value) return;
    updatePin(activePinId.value, pin => ({ ...pin, coordinates }));
  }

  function addPin(coordinates) {
    if (!canAddNewPin.value) return null;

    const newPin = {
      id: generateUUID(),
      coordinates,
      state: "draft",
      messages: [],
      instruction: undefined,
      areasOfInterest: undefined,
      segmentationMaskUrl: undefined,
      highlightAreasEnabled: undefined,
      thinkingText: undefined,
      isNew: true
    };

    pins.value = [...pins.value, newPin];
    activePinId.value = newPin.id;

    if (!isDesktop.value) {
      mobileView.value = "detail";
      isPanelExpanded.value = true;
    }

    const sessionId = sessionIdRef.value;
    const artworkId = artworkIdRef.value;
    if (sessionId && artworkId) {
      trackTransfersPinCreated({
        artworkId,
        sessionId,
        pinId: newPin.id
      });
    }

    return newPin;
  }

  /* eslint-disable-next-line max-lines-per-function */
  function handleDeletePin(pinId) {
    const pin = pins.value.find(p => p.id === pinId);
    if (pin?.state === "processing") {
      abortController?.abort();
    }

    const sessionId = sessionIdRef.value;

    if (!sessionId) {
      deletePin(pinId);
      return;
    }
    const hasBeenSyncedToBackend = (pin?.messages?.length ?? 0) > 0 || pin?.state !== "draft";

    const artworkId = artworkIdRef.value;
    if (!hasBeenSyncedToBackend) {
      if (artworkId) {
        trackTransfersPinDeleted({
          artworkId,
          sessionId,
          pinId,
          pinState: pin?.state ?? "draft",
          hadMessages: false
        });
      }
      deletePin(pinId);
      if (!isDesktop.value) {
        mobileView.value = "list";
      }
      return;
    }

    if (removingPinIds.value.has(pinId)) return;

    removingPinIds.value = new Set(removingPinIds.value).add(pinId);

    removePinApi(sessionId, pinId)
      .then(() => {
        if (artworkId) {
          trackTransfersPinDeleted({
            artworkId,
            sessionId,
            pinId,
            pinState: pin?.state ?? "draft",
            hadMessages: (pin?.messages?.length ?? 0) > 0
          });
        }
        deletePin(pinId);
        clearRemovingPin(pinId);
        if (!isDesktop.value) {
          mobileView.value = "list";
        }
      })
      .catch(() => {
        if (artworkId) {
          trackTransfersPinDeleteFailed({ artworkId, sessionId, pinId });
        }
        clearRemovingPin(pinId);
        toast?.error({
          title: "Failed to remove pin",
          description: "Something went wrong. Please try again."
        });
      });
  }

  function openDeletePinModal(pinId) {
    pendingDeletePinId.value = pinId;
  }

  function closeDeletePinModal() {
    pendingDeletePinId.value = null;
  }

  function confirmDeletePin() {
    if (!pendingDeletePinId.value) return;
    handleDeletePin(pendingDeletePinId.value);
    pendingDeletePinId.value = null;
  }

  function openEditPinModal(pinId) {
    editingPinId.value = pinId;
  }

  function closeEditPinModal() {
    editingPinId.value = null;
  }

  function handleUpdatePinInstruction(instruction) {
    if (!editingPinId.value || !sessionIdRef.value) return;

    isUpdatingPinInstruction.value = true;

    updatePinInstructionApi(sessionIdRef.value, editingPinId.value, instruction)
      .then(() => {
        updatePin(editingPinId.value, pin => ({ ...pin, instruction }));
        editingPinId.value = null;
      })
      .catch(() => {
        toast?.error({
          title: "Failed to update pin",
          description: "Something went wrong. Please try again."
        });
      })
      .finally(() => {
        isUpdatingPinInstruction.value = false;
      });
  }

  // -- Clarification Handlers --

  /* eslint-disable-next-line max-lines-per-function */
  function handleSelectClarificationOption(question, value) {
    const prev = clarificationResponses.value;

    const newResponses = {
      ...prev,
      [question]: {
        selected: value,
        custom: prev[question]?.custom ?? "",
        isTypingCustom: false
      }
    };

    clarificationResponses.value = newResponses;

    const sessionId = sessionIdRef.value;
    const artworkId = artworkIdRef.value;
    const currentPin = activePin.value;
    if (sessionId && artworkId && currentPin) {
      const userCount = currentPin.messages.filter(m => m.role === "user").length;
      const aiCount = currentPin.messages.filter(m => m.role === "ai").length;
      trackTransfersPinClarificationClicked({
        artworkId,
        sessionId,
        pinId: currentPin.id,
        optionValue: value || "",
        turnIndex: userCount + 1,
        userMessagesCount: userCount,
        aiMessagesCount: aiCount,
        optionType: "suggested"
      });
    }

    // Auto-submit for finalize_confirmation clarifications
    if (value !== null) {
      const clarification = pendingPillClarifications.value.find(c => c.question === question);
      if (clarification?.type === "finalize_confirmation") {
        handleSubmit();
        return;
      }
    }

    // Auto-scroll to next unanswered after selecting
    if (value !== null && !getClarificationAnswer(prev[question])) {
      setTimeout(() => {
        const currentIndex = pendingPillClarifications.value.findIndex(
          c => c.question === question
        );
        const reorderedClarifications = [
          ...pendingPillClarifications.value.slice(currentIndex + 1),
          ...pendingPillClarifications.value.slice(0, currentIndex)
        ];
        const nextUnanswered = reorderedClarifications.find(
          c => !getClarificationAnswer(newResponses[c.question])
        );

        if (nextUnanswered && clarificationScrollRef.value) {
          const labelValue = nextUnanswered.label || nextUnanswered.question;
          const targetElement = clarificationScrollRef.value.querySelector(
            `[data-clarification-label="${CSS.escape(labelValue)}"]`
          );
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      }, 100);
    }
  }

  function handleToggleCustomInput(question) {
    clarificationResponses.value = {
      ...clarificationResponses.value,
      [question]: {
        selected: clarificationResponses.value[question]?.selected ?? null,
        custom: clarificationResponses.value[question]?.custom ?? "",
        isTypingCustom: !clarificationResponses.value[question]?.isTypingCustom
      }
    };

    const sessionId = sessionIdRef.value;
    const artworkId = artworkIdRef.value;
    const currentPin = activePin.value;
    if (sessionId && artworkId && currentPin) {
      const userCount = currentPin.messages.filter(m => m.role === "user").length;
      const aiCount = currentPin.messages.filter(m => m.role === "ai").length;
      trackTransfersPinClarificationClicked({
        artworkId,
        sessionId,
        pinId: currentPin.id,
        optionValue: "",
        turnIndex: userCount + 1,
        userMessagesCount: userCount,
        aiMessagesCount: aiCount,
        optionType: "custom"
      });
    }
  }

  function handleCustomInputChange(question, value) {
    clarificationResponses.value = {
      ...clarificationResponses.value,
      [question]: {
        selected: clarificationResponses.value[question]?.selected ?? null,
        custom: value,
        isTypingCustom: true
      }
    };
  }

  function trackPinSelected(pinId, previousPinId, pinState) {
    const sessionId = sessionIdRef.value;
    const artworkId = artworkIdRef.value;
    if (sessionId && artworkId) {
      trackTransfersPinSelected({
        artworkId,
        sessionId,
        pinId,
        previousPinId: previousPinId ?? null,
        pinState
      });
    }
  }

  function calculateUnansweredPositions() {
    const scrollContainer = clarificationScrollRef.value;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    let above = 0;
    let below = 0;

    pendingPillClarifications.value.forEach(clarification => {
      const isUnanswered = !getClarificationAnswer(
        clarificationResponses.value[clarification.question]
      );
      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );
        if (element) {
          const elementRect = element.getBoundingClientRect();
          if (elementRect.bottom < containerRect.top) {
            above += 1;
          } else if (elementRect.top > containerRect.bottom) {
            below += 1;
          }
        }
      }
    });

    unansweredAbove.value = above;
    unansweredBelow.value = below;
  }

  function scrollToFirstUnanswered(direction) {
    const scrollContainer = clarificationScrollRef.value;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();

    for (const clarification of pendingPillClarifications.value) {
      const isUnanswered = !getClarificationAnswer(
        clarificationResponses.value[clarification.question]
      );

      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );

        if (element) {
          const elementRect = element.getBoundingClientRect();
          const isTargetDirection =
            direction === "above"
              ? elementRect.bottom < containerRect.top
              : elementRect.top > containerRect.bottom;

          if (isTargetDirection) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            break;
          }
        }
      }
    }
  }

  // -- Message Submission --

  function formatClarificationResponses() {
    const parts = [];
    const clarifications = [];

    for (const clarification of pendingPillClarifications.value) {
      const value = getClarificationAnswer(clarificationResponses.value[clarification.question]);
      const isCustom =
        clarificationResponses.value[clarification.question]?.isTypingCustom ?? false;
      if (value) {
        const labelText = clarification.label || clarification.question;
        parts.push(`"${labelText}": ${value}`);
        clarifications.push({
          question: clarification.question,
          value,
          option_type: isCustom ? "custom" : "suggested"
        });
      }
    }

    return { clarificationText: parts.join("\n"), clarifications };
  }

  // eslint-disable-next-line max-lines-per-function
  function dispatchPinMessage({
    sessionId,
    artworkId,
    pinId,
    coordinates,
    message,
    isFirstMessage
  }) {
    abortController?.abort();
    abortController = new AbortController();

    const priorPins = pins.value
      .filter(p => p.id !== pinId)
      .map(p => ({
        pinId: p.id,
        coordinates: p.coordinates,
        state: p.state,
        instruction: p.instruction ?? ""
      }));

    submitPinMessage(
      { sessionId, pinId, coordinates, userMessage: message, isFirstMessage, priorPins },
      {
        onAcknowledgment(payload) {
          updatePin(pinId, pin => ({
            ...pin,
            thinkingText: payload.message || undefined
          }));
        },
        // eslint-disable-next-line max-lines-per-function
        onResult(payload) {
          const knownStatuses = ["ready", "preview_ready", "needs_clarification", "unsupported"];
          const mappedStatus = knownStatuses.includes(payload.status) ? payload.status : "ready";
          const isNonGenerativeReady = payload.status === "ready" && !payload.generatedImageUrl;
          let nextState = mappedStatus;
          if (isNonGenerativeReady) {
            nextState = "processing";
          } else if (mappedStatus === "preview_ready") {
            nextState = "ready";
          }

          const payloadClarifications = payload.clarifications ?? [];
          const resultMessage = payload.message || "Done.";

          const askingClarifications = payloadClarifications.filter(
            c => c.status === "asking" && c.options.length > 0
          );
          const hasClarifications =
            nextState === "needs_clarification" && askingClarifications.length > 0;

          updatePin(pinId, pin => {
            const areasOfInterestUpdate = payload.aoiResult
              ? {
                  areasOfInterest: payload.aoiResult,
                  highlightAreasEnabled: pin.highlightAreasEnabled ?? true
                }
              : {};

            return {
              ...pin,
              state: nextState,
              instruction: payload.instruction,
              thinkingText: undefined,
              ...areasOfInterestUpdate,
              messages: [
                ...pin.messages,
                {
                  id: generateUUID(),
                  role: "ai",
                  content: resultMessage,
                  timestamp: Date.now(),
                  ...(hasClarifications && {
                    clarifications: payloadClarifications,
                    textInfo: payload.text
                  })
                }
              ]
            };
          });

          if (hasClarifications) {
            clarificationResponses.value = {};
          }

          savedClarificationResponses = null;
          savedPinState = null;
          savedMessages = null;
          savedComposerText = null;

          const currentPin = pins.value.find(p => p.id === pinId);
          const userCount = (currentPin?.messages ?? []).filter(m => m.role === "user").length;
          const aiCount = (currentPin?.messages ?? []).filter(m => m.role === "ai").length + 1;
          const trackingData = {
            artworkId,
            sessionId,
            pinId,
            userMessagesCount: userCount,
            aiMessagesCount: aiCount
          };

          if (!sessionId || !artworkId) return;

          if (nextState === "ready" || isNonGenerativeReady) {
            trackTransfersPinReady(trackingData);
          } else if (nextState === "unsupported") {
            trackTransfersPinUnsupported(trackingData);
          } else if (nextState === "needs_clarification") {
            trackTransfersPinNeedsClarification({
              ...trackingData,
              optionsCount: askingClarifications.reduce((sum, c) => sum + c.options.length, 0),
              questionType: askingClarifications.map(c => c.type).join(",")
            });
          }

          if (payload.status === "preview_ready" && payload.generatedImageUrl) {
            isPreviewImageLoaded.value = false;
            previewApproval.value = {
              pinId,
              generatedImageUrl: payload.generatedImageUrl,
              instruction: payload.instruction
            };
            isPanelExpanded.value = false;
            trackPinSessionGeneratedImageShown({
              artworkId,
              sessionId,
              generatedImageUrl: payload.generatedImageUrl,
              pinId
            });
          }
          // Non-generative "ready" auto-complete is deferred to onStreamEnd
          // so the backend has time to send a follow-up "preview_ready" event.
        },
        onStreamEnd() {
          // Stream closed without a preview_ready event — transition to "ready"
          // so the escalation card appears.
          const pin = pins.value.find(p => p.id === pinId);
          if (pin?.state === "processing") {
            updatePin(pinId, p => ({ ...p, state: "ready" }));
          }
        },
        onError() {
          const currentPin = pins.value.find(p => p.id === pinId);
          const userCount = (currentPin?.messages ?? []).filter(m => m.role === "user").length;
          const aiCount = (currentPin?.messages ?? []).filter(m => m.role === "ai").length;

          if (sessionId && artworkId) {
            trackTransfersPinError({
              artworkId,
              sessionId,
              pinId,
              errorType: "processing_error",
              userMessagesCount: userCount,
              aiMessagesCount: aiCount
            });
          }

          const previousState = savedPinState ?? "draft";
          const previousMessages = savedMessages;

          updatePin(pinId, pin => ({
            ...pin,
            state: previousState,
            thinkingText: undefined,
            messages: previousMessages ?? pin.messages.slice(0, -1)
          }));

          if (savedClarificationResponses) {
            clarificationResponses.value = savedClarificationResponses;
            savedClarificationResponses = null;
          }

          if (savedComposerText !== null) {
            composerText.value = savedComposerText;
            savedComposerText = null;
          }

          savedPinState = null;
          savedMessages = null;

          toast?.error({ title: "Something went wrong", description: "Please try again." });
        }
      },
      {
        isPinActive: id => pins.value.some(p => p.id === id) && !removingPinIds.value.has(id),
        signal: abortController.signal
      }
    );
  }

  // eslint-disable-next-line max-lines-per-function
  function handleSubmit() {
    const currentActivePin = activePin.value;
    if (!currentActivePin) return;
    if (currentActivePin.state === "processing") return;

    const sessionId = sessionIdRef.value;
    const artworkId = artworkIdRef.value;
    const { clarificationText, clarifications } = formatClarificationResponses();
    const messageText = composerText.value.trim();
    const fullMessage = [clarificationText, messageText].filter(Boolean).join("\n");

    if (!fullMessage) return;

    const pinId = currentActivePin.id;
    const userMessageId = generateUUID();
    const isFirstMessage = currentActivePin.messages.filter(m => m.role === "user").length === 0;
    const turnIndex = currentActivePin.messages.filter(m => m.role === "user").length + 1;
    const userMessagesCount = currentActivePin.messages.filter(m => m.role === "user").length;
    const aiMessagesCount = currentActivePin.messages.filter(m => m.role === "ai").length;

    if (sessionId && artworkId) {
      trackTransfersPinMessageSubmitted({
        artworkId,
        sessionId,
        pinId,
        isFirstMessage,
        turnIndex,
        userMessagesCount,
        aiMessagesCount,
        clarifications
      });
    }

    savedClarificationResponses = { ...clarificationResponses.value };
    savedPinState = currentActivePin.state;
    savedMessages = [...currentActivePin.messages];
    savedComposerText = composerText.value;

    updatePin(pinId, pin => ({
      ...pin,
      state: "processing",
      thinkingText: undefined,
      messages: [
        ...pin.messages.map(m => ({ ...m, clarifications: undefined })),
        { id: userMessageId, role: "user", content: fullMessage, timestamp: Date.now() }
      ]
    }));

    composerText.value = "";
    clarificationResponses.value = {};
    lastEditedPinId.value = pinId;

    if (!sessionId) return;

    dispatchPinMessage({
      sessionId,
      artworkId,
      pinId,
      coordinates: currentActivePin.coordinates,
      message: fullMessage,
      isFirstMessage
    });
  }

  // -- Watchers --

  // Reset expanded state and clear hovered pin when going to list view
  watch(mobileView, newView => {
    if (newView === "list") {
      isPanelExpanded.value = false;
      hoveredPinId.value = null;
      hoveredPinSource.value = null;
    }
  });

  // Auto-scroll messages to bottom when messages change
  watch(
    () => activePin.value?.messages.length,
    () => {
      if (messagesScrollRef.value && activePin.value?.messages.length) {
        nextTick(() => {
          if (messagesScrollRef.value) {
            messagesScrollRef.value.scrollTop = messagesScrollRef.value.scrollHeight;
          }
        });
      }
    }
  );

  // Collapse panel when pin is ready
  watch(
    () => activePin.value?.state,
    newState => {
      if (newState === "ready") {
        isPanelExpanded.value = false;
      }
    }
  );

  // Reset composer when active pin changes
  watch(activePinId, () => {
    composerText.value = "";
    clarificationResponses.value = {};
  });

  // Handle cursor positioning after newline
  watch(composerText, () => {
    if (pendingCursorPosition.value !== null && textareaRef.value) {
      nextTick(() => {
        const pos = pendingCursorPosition.value;
        if (textareaRef.value && pos !== null) {
          textareaRef.value.selectionStart = pos;
          textareaRef.value.selectionEnd = pos;
          textareaRef.value.scrollTop = textareaRef.value.scrollHeight;
        }
        pendingCursorPosition.value = null;
      });
    }
  });

  // Recalculate unanswered positions when clarifications change
  watch([() => pendingPillClarifications.value.length, clarificationResponses], () => {
    if (pendingPillClarifications.value.length > 0) {
      calculateUnansweredPositions();
    }
  });

  // Auto-select incomplete pin
  watch(
    [pins, mobileView, activePinId],
    () => {
      if (pins.value.length === 0) return;

      const incompletePin = pins.value.find(p => p.state !== "ready" && p.state !== "unsupported");
      if (!incompletePin) return;

      if (isDesktop.value) {
        if (!activePinId.value) {
          activePinId.value = incompletePin.id;
          setTimeout(() => {
            const pinElement = pinsContainerRef.value?.querySelector(
              `[data-pin-id="${incompletePin.id}"]`
            );
            pinElement?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }
      } else if (mobileView.value === "list") {
        activePinId.value = incompletePin.id;
        mobileView.value = "detail";
        isPanelExpanded.value = true;
      }
    },
    { deep: true }
  );

  // Reset mobile view when all pins are deleted
  watch(
    () => pins.value.length,
    newLength => {
      if (!isDesktop.value && newLength === 0) {
        mobileView.value = "list";
      }
    }
  );

  // Mobile: set hovered pin for completed pins in detail view
  watch([mobileView, activePin], () => {
    if (!isDesktop.value && mobileView.value === "detail" && activePin.value) {
      const isCompleted =
        activePin.value.state === "ready" || activePin.value.state === "unsupported";
      if (isCompleted) {
        hoveredPinId.value = activePin.value.id;
        hoveredPinSource.value = "panel";
      }
    }
  });

  // Mobile: new pin auto-switches to detail view
  watch(activePinId, newPinId => {
    if (!isDesktop.value && newPinId) {
      const pin = pins.value.find(p => p.id === newPinId);
      if (pin?.isNew && pin?.state === "draft" && pin?.messages.length === 0) {
        mobileView.value = "detail";
        isPanelExpanded.value = true;
      }
    }
  });

  // Auto-submit initial message when first pin is placed (message-first flow)
  let autoSubmitTriggered = false;

  if (pendingInitialMessage) {
    // eslint-disable-next-line max-lines-per-function
    watch([pins, activePinId], () => {
      if (autoSubmitTriggered) return;
      if (!pendingInitialMessage.value) return;
      if (pins.value.length === 0 || !activePinId.value) return;

      const pin = pins.value.find(p => p.id === activePinId.value);
      if (!pin || pin.state !== "draft" || pin.messages.length > 0) return;

      const sessionId = sessionIdRef.value;
      if (!sessionId) return;

      autoSubmitTriggered = true;
      const message = pendingInitialMessage.value;
      pendingInitialMessage.value = null;

      const pinId = pin.id;
      const userMessageId = generateUUID();
      const artworkId = artworkIdRef.value;

      savedPinState = pin.state;
      savedMessages = [...pin.messages];
      savedComposerText = message;

      updatePin(pinId, p => ({
        ...p,
        state: "processing",
        thinkingText: undefined,
        messages: [{ id: userMessageId, role: "user", content: message, timestamp: Date.now() }]
      }));
      lastEditedPinId.value = pinId;

      if (artworkId && sessionId) {
        trackTransfersPinMessageSubmitted({
          artworkId,
          sessionId,
          pinId,
          isFirstMessage: true,
          turnIndex: 1,
          userMessagesCount: 1,
          aiMessagesCount: 0,
          clarifications: []
        });
      }

      dispatchPinMessage({
        sessionId,
        artworkId,
        pinId,
        coordinates: pin.coordinates,
        message,
        isFirstMessage: true
      });
    });
  }

  /**
   * Hydrates pins from an API response (used when editing an existing session).
   * Maps backend pin data to the internal pin format.
   *
   * @param {Array} pinsData - Array of pin objects from the API
   */
  function hydratePins(pinsData) {
    pins.value = pinsData.map(p => {
      const messages = Array.isArray(p.messages)
        ? p.messages
            .filter(m => m?.role !== "ai_ack")
            .map(m => ({
              id: m.id || "",
              role: m.role || "user",
              content: m.content || "",
              timestamp: m.timestamp || Date.now(),
              ...(m.options ? { options: m.options } : {}),
              ...(m.questionType ? { questionType: m.questionType } : {}),
              ...(m.clarifications ? { clarifications: m.clarifications } : {}),
              ...(m.textInfo ? { textInfo: m.textInfo } : {})
            }))
        : [];

      if (messages.length === 0 && p.message) {
        messages.push({
          id: `msg-${p.pinId}`,
          role: "ai",
          content: p.message,
          timestamp: Date.now()
        });
      }

      const state = p.status || p.state || "draft";

      return {
        id: p.pinId || p.id || "",
        coordinates: p.coordinates || { x: 0, y: 0 },
        state,
        instruction: p.instruction || p.summary,
        messages,
        areasOfInterest: p.areaOfInterest,
        segmentationMaskUrl: p.segmentationMaskUrl,
        isNew: false
      };
    });

    activePinId.value = null;
  }

  async function handleApprovePreview() {
    if (!previewApproval.value || isApproving.value || isRejecting.value) return null;
    isApproving.value = true;
    try {
      return await approvePinApi(sessionIdRef.value);
    } catch (err) {
      isApproving.value = false;
      toast?.error?.({ title: "Failed to approve", description: "Please try again." });
      return null;
    }
  }

  async function handleRejectPreview() {
    if (!previewApproval.value || isRejecting.value || isApproving.value) return null;
    isRejecting.value = true;
    try {
      return await rejectPinApi(sessionIdRef.value);
    } catch (err) {
      isRejecting.value = false;
      toast?.error?.({ title: "Failed to reject", description: "Please try again." });
      return null;
    }
  }

  onBeforeUnmount(() => {
    abortController?.abort();
    pins.value = [];
    activePinId.value = null;
    lastEditedPinId.value = null;
  });

  return {
    // Constants
    PIN_COLORS,
    MAX_PINS,

    // State
    isDesktop,
    pins,
    activePinId,
    hoveredPinId,
    hoveredPinSource,
    composerText,
    removingPinIds,
    pendingDeletePinId,
    clarificationResponses,
    mobileView,
    isPanelExpanded,
    editingPinId,
    isUpdatingPinInstruction,
    lastEditedPinId,
    unansweredAbove,
    unansweredBelow,
    showPins,
    previewApproval,
    isApproving,
    isRejecting,
    isPreviewImageLoaded,

    // Refs
    textareaRef,
    pendingCursorPosition,
    clarificationScrollRef,
    messagesScrollRef,
    pinsContainerRef,

    // Computed
    activePin,
    editingPin,
    hasCompletedPin,
    readyPinsCount,
    hasExistingPin,
    hasAiAssistanceOngoing,
    hasUnfinishedPin,
    canAddNewPin,
    canRepositionActivePin,
    lastAiMessageWithClarifications,
    pendingPillClarifications,
    selectedFontValue,
    selectedColorValue,
    hasAnyClarificationResponse,

    // Handlers
    addPin,
    repositionActivePin,
    handleDeletePin,
    openDeletePinModal,
    closeDeletePinModal,
    confirmDeletePin,
    openEditPinModal,
    closeEditPinModal,
    handleUpdatePinInstruction,
    handleSelectClarificationOption,
    handleToggleCustomInput,
    handleCustomInputChange,
    handleSubmit,
    isComposerTextValid,
    trackPinSelected,
    calculateUnansweredPositions,
    scrollToFirstUnanswered,
    hydratePins,
    handleApprovePreview,
    handleRejectPreview
  };
}
